import React, { useState, useMemo } from 'react';
import { Search, Filter, X, Shield, FileText, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { FrameworkBadge } from './FrameworkBadge';

export interface FrameworkStandard {
  id: number;
  framework_name: string;
  standard_id: string;
  standard_number?: string;
  standard_name: string;
  description?: string;
  requirements?: string;
  category?: string;
  page_number?: string;
  section_reference?: string;
  document_url?: string;
  statutory_reference?: string;
}

interface FrameworkListProps {
  frameworks: FrameworkStandard[];
  onSelectFramework?: (framework: FrameworkStandard) => void;
  loading?: boolean;
  compact?: boolean;
}

export const FrameworkList: React.FC<FrameworkListProps> = ({
  frameworks,
  onSelectFramework,
  loading = false,
  compact = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFrameworkType, setSelectedFrameworkType] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedFrameworks, setExpandedFrameworks] = useState<Set<string>>(new Set());

  // Get unique framework types and categories
  const { frameworkTypes, categories } = useMemo(() => {
    const types = new Set(frameworks.map(f => f.framework_name));
    const cats = new Set(frameworks.map(f => f.category).filter(Boolean));
    return {
      frameworkTypes: Array.from(types).sort(),
      categories: Array.from(cats).sort()
    };
  }, [frameworks]);

  // Filter frameworks
  const filteredFrameworks = useMemo(() => {
    return frameworks.filter(framework => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          framework.standard_name.toLowerCase().includes(search) ||
          framework.standard_id.toLowerCase().includes(search) ||
          framework.description?.toLowerCase().includes(search) ||
          framework.framework_name.toLowerCase().includes(search);

        if (!matchesSearch) return false;
      }

      // Framework type filter
      if (selectedFrameworkType !== 'all' && framework.framework_name !== selectedFrameworkType) {
        return false;
      }

      // Category filter
      if (selectedCategory !== 'all' && framework.category !== selectedCategory) {
        return false;
      }

      return true;
    });
  }, [frameworks, searchTerm, selectedFrameworkType, selectedCategory]);

  // Group frameworks by framework_name
  const groupedFrameworks = useMemo(() => {
    const groups: Record<string, FrameworkStandard[]> = {};
    filteredFrameworks.forEach(fw => {
      if (!groups[fw.framework_name]) {
        groups[fw.framework_name] = [];
      }
      groups[fw.framework_name].push(fw);
    });
    return groups;
  }, [filteredFrameworks]);

  // Toggle framework expansion
  const toggleFramework = (frameworkName: string) => {
    const newExpanded = new Set(expandedFrameworks);
    if (newExpanded.has(frameworkName)) {
      newExpanded.delete(frameworkName);
    } else {
      newExpanded.add(frameworkName);
    }
    setExpandedFrameworks(newExpanded);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedFrameworkType('all');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchTerm || selectedFrameworkType !== 'all' || selectedCategory !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading frameworks...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search frameworks, standards, descriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
              hasActiveFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700'
            } hover:bg-gray-50 transition-colors`}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Framework Type Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Framework</label>
              <select
                value={selectedFrameworkType}
                onChange={(e) => setSelectedFrameworkType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Frameworks</option>
                {frameworkTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        Showing {filteredFrameworks.length} of {frameworks.length} standards
      </div>

      {/* Grouped Framework List */}
      {Object.keys(groupedFrameworks).length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-700 mb-1">No frameworks found</h3>
          <p className="text-sm text-gray-500">
            {hasActiveFilters
              ? 'Try adjusting your filters to see more results'
              : 'No frameworks available'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedFrameworks).map(([frameworkName, standards]) => {
            const isExpanded = expandedFrameworks.has(frameworkName);

            return (
              <div key={frameworkName} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Framework Header */}
                <button
                  onClick={() => toggleFramework(frameworkName)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-blue-600" />
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-800">{frameworkName}</h3>
                      <p className="text-sm text-gray-600">{standards.length} standards</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {/* Standards List */}
                {isExpanded && (
                  <div className="border-t border-gray-200 divide-y divide-gray-100">
                    {standards.map((standard) => (
                      <div
                        key={standard.id}
                        className={`p-4 hover:bg-gray-50 transition-colors ${
                          onSelectFramework ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => onSelectFramework?.(standard)}
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FrameworkBadge
                                frameworkName={standard.framework_name}
                                standardId={standard.standard_id}
                                size="sm"
                              />
                              {standard.category && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                  {standard.category}
                                </span>
                              )}
                            </div>
                            <h4 className="font-medium text-blue-600 hover:text-blue-800 underline cursor-pointer">{standard.standard_name}</h4>
                          </div>
                          {standard.document_url && (
                            <a
                              href={standard.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>

                        {standard.description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{standard.description}</p>
                        )}

                        {!compact && standard.requirements && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                            <span className="font-medium">Requirements: </span>
                            {standard.requirements}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                          {standard.statutory_reference && (
                            <span>
                              <FileText className="inline h-3 w-3 mr-1" />
                              {standard.statutory_reference}
                            </span>
                          )}
                          {standard.page_number && (
                            <span>Page: {standard.page_number}</span>
                          )}
                          {standard.section_reference && (
                            <span>Section: {standard.section_reference}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
